import { juloUpdateEntries } from "$/lib/julo-updates"
import { JuloUpdateEntry } from "$/lib/julo-updates"
import { hasSeenPopupOnDevice } from "$/lib/julo-updates/device-popup-seen"
import {
  JuloUpdateReadState,
  JuloUpdateReadStatus,
  Route,
  SetJuloUpdateReadStatus,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

export interface ResolvedJuloUpdate extends JuloUpdateEntry {
  hasBeenRead: boolean
  isManuallyUnread: boolean
}

async function fetchReadState(storeId: string): Promise<JuloUpdateReadState> {
  return request({
    url: populateRoute(Route.JULO_UPDATE__READ_STATUSES, storeId),
    method: "GET",
  })
}

async function setReadStatus(
  storeId: string,
  slug: string,
  data: SetJuloUpdateReadStatus,
): Promise<void> {
  return request({
    url: populateRoute(Route.JULO_UPDATE__SET_READ_STATUS, storeId, slug),
    method: "PATCH",
    data,
  })
}

async function markAllAsRead(storeId: string, slugs: string[]): Promise<void> {
  return request({
    url: populateRoute(Route.JULO_UPDATE__MARK_ALL_READ, storeId),
    method: "PATCH",
    data: { slugs },
  })
}

const juloUpdateQueries = {
  all: () => ["julo-update"],
  readStatuses: (storeId: string) =>
    queryOptions({
      queryKey: [...juloUpdateQueries.all(), "read-statuses", storeId],
      queryFn: () => fetchReadState(storeId),
      staleTime: 5000,
    }),
}

const resolveEntries = (
  readStatuses: JuloUpdateReadStatus[] | undefined,
): ResolvedJuloUpdate[] => {
  const byslug = new Map(
    (readStatuses ?? []).map((status) => [status.slug, status]),
  )
  return juloUpdateEntries.map((entry) => {
    const status = byslug.get(entry.slug)
    return {
      ...entry,
      hasBeenRead: status !== undefined,
      isManuallyUnread: status?.isManuallyUnread ?? false,
    }
  })
}

const countUnread = (entries: ResolvedJuloUpdate[]): number =>
  entries.filter((entry) => !entry.hasBeenRead || entry.isManuallyUnread).length

export const useJuloUpdates = (storeId: string) => {
  const { data, ...rest } = useQuery(juloUpdateQueries.readStatuses(storeId))
  const updates = resolveEntries(data?.readStatuses)
  return { updates, unreadCount: countUnread(updates), ...rest }
}

export const useJuloUpdatesUnreadCount = (storeId: string) => {
  const { data } = useQuery(juloUpdateQueries.readStatuses(storeId))
  return countUnread(resolveEntries(data?.readStatuses))
}

// Never-read entries the user should get as a pop-up dialog. Empty until the
// read state has loaded, so nothing pops up from a stale or missing cache.
// Entries flagged `showPopupOncePerDevice` also pop up on every device that
// hasn't shown them yet, even when the user already read them elsewhere —
// stores sharing one login across devices must all see them.
export const useJuloUpdatesToPopUp = (storeId: string) => {
  const { data } = useQuery(juloUpdateQueries.readStatuses(storeId))
  if (!data) return []
  return resolveEntries(data.readStatuses).filter(
    (update) =>
      (!update.hasBeenRead && update.date >= data.popUpFromDate) ||
      (update.showPopupOncePerDevice && !hasSeenPopupOnDevice(update.slug)),
  )
}

export const useSetJuloUpdateReadStatus = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      slug,
      isManuallyUnread,
    }: {
      slug: string
      isManuallyUnread: boolean
    }) => setReadStatus(storeId, slug, { isManuallyUnread }),
    onSettled: () => {
      queryClient.invalidateQueries(juloUpdateQueries.readStatuses(storeId))
    },
  })
}

export const useMarkJuloUpdatesAsRead = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (slugs: string[]) => markAllAsRead(storeId, slugs),
    onSettled: () => {
      queryClient.invalidateQueries(juloUpdateQueries.readStatuses(storeId))
    },
  })
}

export default juloUpdateQueries
